import React, { useState, useEffect, useRef } from 'react';
import ContainerFlex from '../containerFlex';
import * as St from './styles';

export interface IOption {
  readonly id?: string | number;
  value: string;
  label?: string;
}

export type AutoCompleteSelectProps = {
  name?: string;
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  options: IOption[];
  value?: IOption[];
  onChange?: (options: IOption[]) => void;
  onFocus?: (isOpen: boolean) => void;
  readOnly?: boolean;
  heightWhenOpen?: string;
  continerOptionsStyle?: React.CSSProperties;
  showChips?: boolean;
  closeWhenSelect?: boolean;
  btnClear?: boolean;
  collapsible?: boolean;
};

const AutoCompleteSelect = ({
  label,
  placeholder = 'Pesquisar...',
  value = [],
  errorMessage,
  onChange,
  readOnly,
  options = [],
  disabled,
  heightWhenOpen = '250px',
  continerOptionsStyle,
  onFocus,
  name,
  showChips = true,
  closeWhenSelect = false,
  btnClear = true,
  collapsible = false,
}: AutoCompleteSelectProps): JSX.Element => {
  const [isFocused, setIsFocused] = useState(collapsible);
  const [filteredOptions, setFilteredOptions] = useState<IOption[]>(
    () => options,
  );
  const [selectedOptions, setSelectedOptions] = useState<IOption[]>(
    () => value,
  );
  const [filterText, setFilterText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Sincroniza valor externo
  useEffect(() => {
    setSelectedOptions(value || []);
  }, [value]);

  // Sincroniza opções quando mudam
  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  // Notifica quando o foco muda
  useEffect(() => {
    if (onFocus) {
      onFocus(isFocused);
    }
  }, [isFocused, onFocus]);

  const handleToggleFocus = () => {
    if (!readOnly && !disabled) {
      setIsFocused((prev) => !prev);
    }
  };

  const reorderOptionsWithSelected = (
    optionsToReorder: IOption[],
    selected: IOption[],
  ): IOption[] => {
    const selectedValues = new Set(selected.map((o) => o.value));
    const selectedOpts = optionsToReorder.filter((o) =>
      selectedValues.has(o.value),
    );
    const unselectedOpts = optionsToReorder.filter(
      (o) => !selectedValues.has(o.value),
    );
    return [...selectedOpts, ...unselectedOpts];
  };

  const handleSelectOption = (option: IOption) => {
    const isAlreadySelected = selectedOptions.some(
      (o) => o.value === option.value,
    );

    let newSelectedOptions: IOption[];

    if (isAlreadySelected) {
      newSelectedOptions = selectedOptions.filter(
        (o) => o.value !== option.value,
      );
    } else {
      newSelectedOptions = [...selectedOptions, option];
    }

    setSelectedOptions(newSelectedOptions);

    if (onChange) {
      onChange(newSelectedOptions);
    }

    // Retorna a lista ao estado inicial, mas com as selecionadas em primeiro
    setFilterText('');
    const reorderedOptions = reorderOptionsWithSelected(
      options,
      newSelectedOptions,
    );
    setFilteredOptions(reorderedOptions);

    if (closeWhenSelect) {
      setIsFocused(false);
    }
  };

  const handleFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: filterValue } = e.target;
    setFilterText(filterValue);

    if (!filterValue.trim()) {
      const reordered = reorderOptionsWithSelected(options, selectedOptions);
      setFilteredOptions(reordered);
      return;
    }

    const filtered = options.filter((option) =>
      option.value
        .toLowerCase()
        .replaceAll(' ', '')
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .trim()
        .includes(filterValue.toLowerCase().replaceAll(' ', '').trim()),
    );

    const reordered = reorderOptionsWithSelected(filtered, selectedOptions);
    setFilteredOptions(reordered);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOptions([]);
    setFilterText('');

    if (onChange) {
      onChange([]);
    }
  };

  const isOptionSelected = (option: IOption) =>
    selectedOptions.some((o) => o.value === option.value);

  return (
    <St.Container
      data-autocomplete-container
      disabled={disabled}
      isFocused={isFocused}
      colorLine="primary"
      colorLabel="black"
    >
      {label && <label htmlFor={name}>{label}</label>}

      <ContainerFlex
        row
        space
        style={{
          borderBottom: '1px solid #e71037',
          paddingBottom: '8px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: 'transparent',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {showChips && selectedOptions.length > 0 && (
          <div
            style={{ 
              display: 'inline-flex', 
              gap: '4px', 
              flexWrap: 'wrap', 
              flex: 1,
              alignItems: 'center',
            }}
          >
            {selectedOptions.map((option) => (
              <span
                key={option.id || option.value}
                style={{
                  backgroundColor: '#e71037',
                  color: '#fff',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label || option.value}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectOption(option);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    padding: 0,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <input
          ref={inputRef}
          type="text"
          placeholder={selectedOptions.length === 0 ? placeholder : ''}
          value={filterText}
          onChange={handleFilter}
          onFocus={() => !collapsible && setIsFocused(true)}
          onBlur={() => !collapsible && setIsFocused(false)}
          onClick={() => !collapsible && handleToggleFocus()}
          disabled={disabled}
          readOnly={readOnly}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            fontSize: '16px',
            color: '#4a4b4a',
            marginTop: '12px',
            minWidth: '100px',
          }}
        />

        {btnClear && selectedOptions.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              background: 'none',
              border: 'none',
              color: '#999',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0 4px',
              alignSelf: 'flex-end',
            }}
          >
            ✕
          </button>
        )}

        <St.IconRight
          onMouseDown={(e) => collapsible && e.preventDefault()}
          onClick={handleToggleFocus}
          style={{
            transform: isFocused ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E"
          alt="chevron"
        />
      </ContainerFlex>

      <St.ContainerOptions
        isFocused={isFocused}
        collapsible={collapsible}
        heightWhenOpen={heightWhenOpen}
        style={continerOptionsStyle}
      >
        <div>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const isSelected = isOptionSelected(option);
              return (
                <div
                  key={option.id || option.value}
                  className="option"
                  onClick={() => handleSelectOption(option)}
                  style={{
                    backgroundColor: isSelected ? '#f1f1f1' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <St.CheckBox
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleSelectOption(option)}
                  />
                  <span style={{ flex: 1 }}>
                    {option.label || option.value}
                  </span>
                </div>
              );
            })
          ) : (
            <div
              style={{
                padding: '10px',
                color: '#999',
                textAlign: 'center',
              }}
            >
              Sem resultados
            </div>
          )}
        </div>
      </St.ContainerOptions>

      {errorMessage && <St.ErrorMessage>{errorMessage}</St.ErrorMessage>}
    </St.Container>
  );
};

export default AutoCompleteSelect;
